import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ApiEnvelope,
  Chip,
  ProductSet,
  ProductSetPreview,
  ProductSetSummary,
  SaveProductSetRequest,
} from '@seller-portal/types';
import { api } from '@/services/api';

/** Product sets (spec S05–S07) through the Merx API's seller-scoped routes. */

const key = (sellerId: string) => ['seller', sellerId, 'sets'] as const;

export function useSets(sellerId: string) {
  return useQuery({
    queryKey: key(sellerId),
    queryFn: async () =>
      (await api.get<ApiEnvelope<ProductSetSummary[]>>(`/sellers/${sellerId}/sets`)).data.data,
  });
}

/** One set; idle without an id (a new set has none), so the list is never asked for by mistake. */
export function useSet(sellerId: string, id: string | undefined) {
  return useQuery({
    enabled: id !== undefined && id !== '',
    queryKey: [...key(sellerId), id],
    queryFn: async () =>
      (
        await api.get<ApiEnvelope<{ set: ProductSet; preview: ProductSetPreview }>>(
          `/sellers/${sellerId}/sets/${id}`
        )
      ).data.data,
  });
}

export type PreviewRequest =
  | { kind: 'rule'; chips: Chip[] }
  | { kind: 'list'; itemCodes: string[] };

export function usePreview(sellerId: string, request: PreviewRequest | null) {
  return useQuery({
    queryKey: [...key(sellerId), 'preview', request],
    queryFn: async () =>
      (await api.post<ApiEnvelope<ProductSetPreview>>(`/sellers/${sellerId}/sets/preview`, request))
        .data.data,
    enabled: request !== null,
  });
}

export function useSaveSet(sellerId: string, id?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: SaveProductSetRequest) => {
      const res = id
        ? await api.patch<ApiEnvelope<ProductSet>>(`/sellers/${sellerId}/sets/${id}`, body)
        : await api.post<ApiEnvelope<ProductSet>>(`/sellers/${sellerId}/sets`, body);
      return res.data.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key(sellerId) }),
  });
}

export function useDeleteSet(sellerId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/sellers/${sellerId}/sets/${id}`);
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key(sellerId) }),
  });
}
